/**
 * ScimConfigPage
 *
 * Admin page to configure SCIM 2.0 provisioning for this org.
 * Allows selecting a provider, enabling SCIM (generates a bearer token shown once),
 * rotating the token, and disabling SCIM.
 *
 * Route: /settings/scim  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import { Copy, RefreshCw, Trash2, CheckCircle, ShieldOff, Zap } from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import { motion, AnimatePresence } from "framer-motion";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import Loader from "../../components/Loader/Loader";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import {
  getScimConfig,
  enableScim,
  rotateScimToken,
  disableScim,
} from "../../api/adminScim";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../router/routes";
import logger from "../../services/Logger";

const PROVIDER_LABELS = {
  azure_ad: "Azure AD",
  okta: "Okta",
};

// ─── One-time token display ───────────────────────────────────────────────────
const TokenReveal = ({ token, endpoint, onDismiss }) => {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(token).then(() => {
      setCopyError(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      // This token is shown exactly once (per the warning banner below) —
      // a silently failed copy previously gave zero indication that nothing
      // was actually placed on the clipboard, risking the admin dismissing
      // this dialog thinking they'd saved a token they never actually copied.
      logger.error("ScimConfigPage", "Failed to copy SCIM token to clipboard", { error: err.message });
      setCopyError(true);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay/40 backdrop-blur-sm">
      <ModalWrapper title="SCIM Bearer Token" onClose={onDismiss}>
        <div className="p-6 flex flex-col gap-4">
          <div className="flex items-start gap-3 p-3 rounded-xl bg-fill-hex-fef3c7 dark:bg-fill-hex-d97706/10 border border-edge-amber-200 dark:border-edge-hex-d97706/40">
            <ShieldOff className="w-4 h-4 text-ink-hex-d97706 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-ink-amber-800 dark:text-ink-amber-300 leading-relaxed">
              Copy this token now. It will never be shown again. If you lose it, generate a new token — this one will be invalidated.
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold text-ink-gray-700 dark:text-ink-gray-300 mb-1.5">Bearer Token</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono bg-surface-code-muted dark:bg-surface-code-muted text-ink-ink dark:text-ink-gray-200 p-3 rounded-xl break-all border border-edge-gray-200 dark:border-edge-gray-700 select-all">
                {token}
              </code>
              <button
                onClick={copy}
                className="p-2 rounded-xl bg-fill-blue-50 dark:bg-fill-blue-600/10 text-ink-blue-600 hover:bg-fill-blue-100 dark:hover:bg-fill-blue-600/20 transition-colors flex-shrink-0"
                title="Copy token"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            {copyError && (
              <p className="text-xs text-ink-red-600 dark:text-ink-red-300 mt-1.5">
                Couldn&apos;t copy automatically — select the token text above and copy it manually.
              </p>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold text-ink-gray-700 dark:text-ink-gray-300 mb-1.5">SCIM Endpoint URL</p>
            <code className="block text-xs font-mono bg-surface-code-muted dark:bg-surface-code-muted text-ink-ink dark:text-ink-gray-200 p-3 rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 select-all">
              {window.location.origin}{endpoint}
            </code>
          </div>

          <div className="flex justify-end">
            <PrimaryButton onClick={onDismiss}>I've saved the token</PrimaryButton>
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};

// ─── Confirm disable dialog ───────────────────────────────────────────────────
const ConfirmDisableModal = ({ onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay/40 backdrop-blur-sm">
    <ModalWrapper title="Disable SCIM Provisioning" onClose={onCancel}>
      <div className="p-6 flex flex-col gap-4">
        <p className="text-sm text-ink-gray-700 dark:text-ink-gray-300">
          This removes the SCIM credentials and stops accepting provisioning pushes from your IdP.
          Existing group memberships created via SCIM are <span className="font-semibold">not</span> removed.
        </p>
        <div className="flex gap-3 justify-end">
          <SecondaryButton onClick={onCancel} disabled={loading}>Cancel</SecondaryButton>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-xl bg-fill-red-600 text-ink-on-color hover:bg-fill-red-700 transition-colors disabled:opacity-50"
          >
            {loading ? "Disabling…" : "Disable SCIM"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  </div>
);

// ─── Confirm rotate dialog ────────────────────────────────────────────────────
const ConfirmRotateModal = ({ onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay/40 backdrop-blur-sm">
    <ModalWrapper title="Rotate SCIM Token" onClose={onCancel}>
      <div className="p-6 flex flex-col gap-4">
        <p className="text-sm text-ink-gray-700 dark:text-ink-gray-300">
          The current token will be invalidated immediately. Your IdP will stop syncing until you paste the new token into its provisioning settings.
        </p>
        <div className="flex gap-3 justify-end">
          <SecondaryButton onClick={onCancel} disabled={loading}>Cancel</SecondaryButton>
          <PrimaryButton onClick={onConfirm} disabled={loading}>
            {loading ? "Rotating…" : "Rotate Token"}
          </PrimaryButton>
        </div>
      </div>
    </ModalWrapper>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const ScimConfigPage = () => {
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const orgAvailable = UIStore((state) => state.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [rotating, setRotating] = useState(false);

  const [config, setConfig] = useState(null);
  const [provider, setProvider] = useState("azure_ad");

  const [revealToken, setRevealToken] = useState(null);   // { token, endpoint }
  const [showConfirmDisable, setShowConfirmDisable] = useState(false);
  const [showConfirmRotate, setShowConfirmRotate] = useState(false);

  // Guard: redirect non-admins
  useEffect(() => {
    if (orgAvailable === false) {
      navigate(ROUTES.SETTINGS.MODELS, { replace: true });
    }
  }, [orgAvailable, navigate]);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getScimConfig();
      setConfig(data);
      if (data?.provider) setProvider(data.provider);
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load SCIM configuration.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleEnable = async () => {
    setSaving(true);
    try {
      const data = await enableScim(provider);
      showFeedback("SCIM provisioning enabled.", "success");
      setRevealToken({ token: data.token, endpoint: data.endpoint });
      await fetchConfig();
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to enable SCIM.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async () => {
    setRotating(true);
    try {
      const data = await rotateScimToken();
      showFeedback("Token rotated. Update your IdP provisioning settings.", "success");
      setShowConfirmRotate(false);
      setRevealToken({ token: data.token, endpoint: "/scim/v2" });
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to rotate token.", "error");
    } finally {
      setRotating(false);
    }
  };

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await disableScim();
      showFeedback("SCIM provisioning disabled.", "success");
      setShowConfirmDisable(false);
      await fetchConfig();
    } catch {
      showFeedback("Failed to disable SCIM.", "error");
    } finally {
      setDisabling(false);
    }
  };

  const lastSync = config?.last_sync_at
    ? new Date(config.last_sync_at).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "Never";

  return (
    <SettingsPageShell title="SCIM Provisioning" description="Automate user provisioning and deprovisioning using the SCIM 2.0 standard, keeping your identity provider in sync with Photon AI membership." maxWidth="3xl">
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {/* Status banner */}
          <AnimatePresence>
            {config ? (
              <motion.div
                key="configured"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="flex items-start gap-3 p-4 rounded-xl bg-fill-green-100 dark:bg-fill-green-600/10 border border-edge-green-300 dark:border-edge-green-600/40"
              >
                <CheckCircle className="w-5 h-5 text-ink-green-600 dark:text-ink-green-300 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-ink-green-700 dark:text-ink-green-300">
                    Active — <span className="font-semibold">{PROVIDER_LABELS[config.provider] ?? config.provider}</span>
                  </p>
                  <p className="text-xs text-ink-green-600/70 dark:text-ink-green-300/60 mt-0.5">
                    Last sync: {lastSync}
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="not-configured"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="flex items-center gap-3 p-4 rounded-xl bg-surface-muted-slate dark:bg-surface-muted-slate/40 border border-edge-gray-200 dark:border-edge-gray-700"
              >
                <Zap className="w-5 h-5 text-ink-gray-400 flex-shrink-0" />
                <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400">
                  Not configured. Select your identity provider and click Enable to generate a bearer token.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Config panel */}
          <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl p-5 flex flex-col gap-5">
            {/* Provider selector */}
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">Identity Provider</p>
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                Select the IdP that will push group membership changes to Photon via SCIM 2.0.
              </p>
              <div className="flex gap-3 mt-1">
                {Object.entries(PROVIDER_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => !config && setProvider(key)}
                    disabled={!!config}
                    className={`px-4 py-2 text-sm font-medium rounded-xl border transition-colors ${
                      provider === key
                        ? "bg-fill-gradient-primary text-ink-on-primary border-edge-gradient-primary"
                        : "bg-fill-white dark:bg-fill-deep-surface text-ink-gray-700 dark:text-ink-gray-300 border-edge-gray-200 dark:border-edge-gray-700 hover:border-edge-gradient-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 justify-end flex-wrap">
              {config && (
                <>
                  <SecondaryButton
                    onClick={() => setShowConfirmRotate(true)}
                    disabled={rotating || disabling}
                    className="flex items-center gap-1.5"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Rotate Token
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => setShowConfirmDisable(true)}
                    disabled={disabling || rotating}
                    className="flex items-center gap-1.5 text-ink-red-600 hover:text-ink-red-700"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Disable
                  </SecondaryButton>
                </>
              )}
              {!config && (
                <PrimaryButton onClick={handleEnable} disabled={saving}>
                  {saving ? "Enabling…" : "Enable SCIM"}
                </PrimaryButton>
              )}
            </div>
          </div>

          {/* Info note */}
          <div className="text-xs text-ink-gray-500 dark:text-ink-gray-400 leading-relaxed space-y-1">
            <p className="font-medium text-ink-gray-700 dark:text-ink-gray-300">How SCIM provisioning works</p>
            <p>
              Once enabled, configure your IdP to push group changes to Photon's SCIM endpoint using the bearer token
              generated here. Your IdP will automatically add and remove members as changes happen in your directory.
            </p>
            <p>
              SCIM-managed groups are labelled <span className="font-semibold">IdP / SCIM</span> in the Groups list.
              Membership for these groups cannot be edited manually — changes must go through your IdP.
            </p>
            <p>
              For step-by-step setup instructions, see the{" "}
              <a
                href="https://docs.photon.ai/admin/scim-provisioning"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-ink-gradient-primary dark:text-ink-blue-300"
              >
                SCIM Provisioning guide
              </a>.
            </p>
          </div>
        </div>
      )}

      {/* One-time token reveal */}
      {revealToken && (
        <TokenReveal
          token={revealToken.token}
          endpoint={revealToken.endpoint}
          onDismiss={() => setRevealToken(null)}
        />
      )}

      {showConfirmDisable && (
        <ConfirmDisableModal
          loading={disabling}
          onConfirm={handleDisable}
          onCancel={() => setShowConfirmDisable(false)}
        />
      )}

      {showConfirmRotate && (
        <ConfirmRotateModal
          loading={rotating}
          onConfirm={handleRotate}
          onCancel={() => setShowConfirmRotate(false)}
        />
      )}
    </SettingsPageShell>
  );
};

export default ScimConfigPage;
